import { httpClient } from "@bw/shared/api";
import type { RatePercent } from "@bw/shared/types";
import type { CourtBoardResponse, Schema } from "@bw/shared/types/generated";

export type { CourtBoardResponse };

export type Court = Schema["CourtResponse"];
export type CourtCreate = Schema["CourtCreate"];
export type CourtUpdate = Schema["CourtUpdate"];
export type CourtStatus = CourtCreate["status"];
export type CourtBulkCreateRequest = Schema["CourtBulkCreateRequest"];
export type CourtBulkValidationResponse = Schema["CourtBulkValidationResponse"];
export type CourtBulkCreateResponse = Schema["CourtBulkCreateResponse"];
export type CourtBulkConflictResponse = Schema["CourtBulkConflictResponse"];

export interface FacilityAddress {
  line1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

export type QueueAutoCourtStrategy =
  Schema["QueueConfigSet"]["queue_auto_court_strategy"];
export type QueueAutoLateJoinTimingPolicy =
  Schema["QueueConfigSet"]["queue_auto_late_join_timing_policy"];

type GeneratedFacilityResponse = Schema["FacilityResponse"];

export type FacilityDetail = GeneratedFacilityResponse & {
  enabled_services: string[];
};
export type FacilityApprovalStatus =
  Schema["FacilityResponse"]["approval_status"];
export type FacilityApprovalReadinessItem =
  Schema["FacilityApprovalReadinessItem"];
export type FacilityApprovalReadinessResponse =
  Schema["FacilityApprovalReadinessResponse"];

function normalizeFacilityResponse(
  response: GeneratedFacilityResponse,
): FacilityDetail {
  const duration = response.queue_session_duration_minutes;
  const minimumRemaining = response.queue_minimum_remaining_minutes;
  const target = response.queue_auto_target_occupancy;
  const minimumStarting = response.queue_auto_minimum_starting_occupancy;
  const maximumWait = response.queue_auto_max_wait_minutes;
  if (
    !["manual", "auto", "self_serve"].includes(
      response.queue_assignment_mode,
    ) ||
    !["fill_existing", "prefer_fresh", "fresh_only"].includes(
      response.queue_auto_court_strategy,
    ) ||
    !Number.isInteger(duration) ||
    duration < 2 ||
    duration > 720 ||
    !Number.isInteger(minimumRemaining) ||
    minimumRemaining < 1 ||
    minimumRemaining > 719 ||
    minimumRemaining >= duration ||
    !Number.isInteger(target) ||
    target < 2 ||
    target > 6 ||
    !Number.isInteger(minimumStarting) ||
    minimumStarting < 1 ||
    minimumStarting > target ||
    !["fresh_block", "extend_existing", "inherit_remaining"].includes(
      response.queue_auto_late_join_timing_policy,
    ) ||
    !Number.isInteger(maximumWait) ||
    maximumWait < 0 ||
    maximumWait > 120
  ) {
    throw new Error(
      "Facility response contained an incomplete or invalid queue policy.",
    );
  }
  return {
    ...response,
    enabled_services: response.enabled_services ?? [],
  };
}

// Request/response for the per-court player-allocation setter, aliased to the
// generated OpenAPI types (ADR-005) so the body/response can't drift from
// backend common/models.py. PlayerAllocationConfigSet is the PUT body
// ({enabled, max_count}); PlayerAllocationConfig is the echoed saved config.
export type PlayerAllocationConfig = Schema["PlayerAllocationConfig"];

export type PlayerAllocationConfigSet = Schema["PlayerAllocationConfigSet"];

export type QueueConfigSet = Schema["QueueConfigSet"];
export type QueueRecoveryReason = Schema["QueueRecoveryReasonResponse"];
export type QueueRecoveryReasonInput = Schema["QueueRecoveryReasonInput"];
export type QueueRecoveryReasonsReplace =
  Schema["QueueRecoveryReasonsReplaceRequest"];

export const ALL_SERVICES = [
  "court_reservation",
  "open_play",
  "memberships",
  "coaching",
  "classes",
] as const;

// ---------------------------------------------------------------------------
// Multi-facility (F2) — list an org's facilities + create a new one.
//   GET  /facilities/{orgId}/list   (org-scoped facility list)
//   POST /facilities                (FacilityCreate; organization_id in body)
// FacilityListItem is the row shape the org dashboard + selector consume.
// ---------------------------------------------------------------------------

// The org-list endpoint returns complete FacilityResponse rows. Keep this alias
// generated so operational consumers (including queue assignment mode) cannot
// silently lose server fields behind a hand-written subset.
export type FacilityListItem = Schema["AuthorizedFacilityResponse"];

export type FacilityCreateInput = Schema["FacilityCreateRequest"];

// Structured address selected from the verifier. Both coordinates are required
// by the generated public creation contract.
export type FacilityAddressInput = Schema["GeocodedAddress"];

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export function listFacilitiesByOrg(
  orgId: string,
): Promise<FacilityListItem[]> {
  return httpClient
    .get<unknown>(`/facilities/${orgId}/list`)
    .then(asArray<FacilityListItem>);
}

export function createFacility(
  data: FacilityCreateInput,
): Promise<FacilityListItem> {
  return httpClient.post<FacilityListItem>("/facilities", data);
}

export function getFacility(facilityId: string): Promise<FacilityDetail> {
  return httpClient
    .get<GeneratedFacilityResponse>(`/facilities/${facilityId}`)
    .then(normalizeFacilityResponse);
}

export function getFacilityApprovalReadiness(
  facilityId: string,
): Promise<FacilityApprovalReadinessResponse> {
  return httpClient.get<FacilityApprovalReadinessResponse>(
    `/facilities/${encodeURIComponent(facilityId)}/approval-readiness`,
  );
}

export function submitFacilityForReview(
  facilityId: string,
): Promise<GeneratedFacilityResponse> {
  return httpClient.post<GeneratedFacilityResponse>(
    `/facilities/${encodeURIComponent(facilityId)}/submit-review`,
  );
}

// Mutable facility-identity fields. location is the composed display string;
// address is the structured + verified body (with lat/lng). timezone + name +
// facility_type round out what the onboarding facility panel edits.
export interface FacilityUpdateInput {
  name?: string;
  location?: string;
  support_email?: string | null;
  support_phone?: string | null;
  timezone?: string;
  facility_type?: string;
  address?: FacilityAddressInput;
  enabled_services?: string[];
  // Backend FacilityUpdate accepts only these operational transitions. Keep the
  // field tied to the generated request surface while narrowing presentation
  // callers to the two supported values.
  status?: Exclude<Schema["FacilityUpdate"]["status"], null> &
    ("active" | "disabled");
  // Whether front-desk staff can check players into a drop-in session. Mirrors
  // the backend FacilityUpdate.staff_check_in_available (Optional[bool]) —
  // required for the "staff_check_in" / "hybrid" drop-in check-in modes.
  staff_check_in_available?: boolean;
}

export function updateFacility(
  facilityId: string,
  data: FacilityUpdateInput,
): Promise<FacilityDetail> {
  return httpClient
    .patch<GeneratedFacilityResponse>(`/facilities/${facilityId}`, data)
    .then(normalizeFacilityResponse);
}

// PUT /facilities/{facility_id}/player-allocation — enable/disable per-court
// player allocation and set the max players-per-court cap. Body
// {enabled, max_count}; the server requires an integer 1<=max_count<=50 when
// enabled (else 400) and treats max_count as null when disabled. Gated by
// facility:manage server-side. Returns the saved PlayerAllocationConfig.
export function setPlayerAllocation(
  facilityId: string,
  config: PlayerAllocationConfigSet,
): Promise<PlayerAllocationConfig> {
  return httpClient.put<PlayerAllocationConfig>(
    `/facilities/${facilityId}/player-allocation`,
    config,
  );
}

// PUT /facilities/{facility_id}/queue-config — atomically configure assignment
// mode, court-block duration, late-join minimum, and the complete Auto policy.
// Gated by facility:manage server-side. Returns the generated FacilityResponse.
export function setQueueConfig(
  facilityId: string,
  body: QueueConfigSet,
): Promise<FacilityDetail> {
  return httpClient
    .put<GeneratedFacilityResponse>(
      `/facilities/${facilityId}/queue-config`,
      body,
    )
    .then(normalizeFacilityResponse);
}

export function listQueueRecoveryReasons(
  facilityId: string,
): Promise<QueueRecoveryReason[]> {
  return httpClient.get<QueueRecoveryReason[]>(
    `/facilities/${encodeURIComponent(facilityId)}/queue-recovery-reasons`,
  );
}

export function replaceQueueRecoveryReasons(
  facilityId: string,
  body: QueueRecoveryReasonsReplace,
): Promise<QueueRecoveryReason[]> {
  return httpClient.put<QueueRecoveryReason[]>(
    `/facilities/${encodeURIComponent(facilityId)}/queue-recovery-reasons`,
    body,
  );
}

export function listCourts(facilityId: string): Promise<Court[]> {
  return httpClient.get<Court[]>(`/facilities/${facilityId}/courts`);
}

export function createCourt(
  facilityId: string,
  data: CourtCreate,
): Promise<Court> {
  return httpClient.post<Court>(`/facilities/${facilityId}/courts`, data);
}

export function validateBulkCourts(
  facilityId: string,
  data: CourtBulkCreateRequest,
): Promise<CourtBulkValidationResponse> {
  return httpClient.post<CourtBulkValidationResponse>(
    `/facilities/${encodeURIComponent(facilityId)}/courts/bulk/validate`,
    data,
  );
}

export function createCourtsBulk(
  facilityId: string,
  data: CourtBulkCreateRequest,
): Promise<CourtBulkCreateResponse> {
  return httpClient.post<CourtBulkCreateResponse>(
    `/facilities/${encodeURIComponent(facilityId)}/courts/bulk`,
    data,
  );
}

export function updateCourt(
  courtId: string,
  data: CourtUpdate,
): Promise<Court> {
  return httpClient.patch<Court>(`/facilities/courts/${courtId}`, data);
}

export function deleteCourt(courtId: string): Promise<void> {
  return httpClient.delete<void>(`/facilities/courts/${courtId}`);
}

// GET /facilities/{facilityId}/drop-in/court-board — canonical operational,
// occupancy, and interaction state for every non-deleted court.
export function getCourtBoard(facilityId: string): Promise<CourtBoardResponse> {
  return httpClient.get<CourtBoardResponse>(
    `/facilities/${facilityId}/drop-in/court-board`,
  );
}

export type FacilityOperatingHoursChangeCommand =
  Schema["FacilityOperatingHoursChangeCommand"];
export type FacilityOperatingHoursChangePreview =
  Schema["FacilityOperatingHoursChangePreview"];
export type FacilityOperatingHoursDateProjection =
  Schema["FacilityOperatingHoursDateProjection"];
export type FacilityOperatingHoursProjectionPage =
  Schema["FacilityOperatingHoursProjectionPage"];
export type FacilityOperatingHoursSnapshot =
  Schema["FacilityOperatingHoursSnapshot"];

export function getOperatingHours(
  facilityId: string,
): Promise<FacilityOperatingHoursSnapshot> {
  return httpClient.get<FacilityOperatingHoursSnapshot>(
    `/facilities/${encodeURIComponent(facilityId)}/operating-hours`,
  );
}

export function previewOperatingHours(
  facilityId: string,
  command: FacilityOperatingHoursChangeCommand,
): Promise<FacilityOperatingHoursChangePreview> {
  return httpClient.post<FacilityOperatingHoursChangePreview>(
    `/facilities/${encodeURIComponent(facilityId)}/operating-hours/preview`,
    command,
  );
}

export function setOperatingHours(
  facilityId: string,
  command: FacilityOperatingHoursChangeCommand,
): Promise<FacilityOperatingHoursSnapshot> {
  return httpClient.put<FacilityOperatingHoursSnapshot>(
    `/facilities/${encodeURIComponent(facilityId)}/operating-hours`,
    command,
  );
}

export function getOperatingHoursProjection(
  facilityId: string,
  localDate: string,
): Promise<FacilityOperatingHoursDateProjection> {
  return httpClient.get<FacilityOperatingHoursDateProjection>(
    `/facilities/${encodeURIComponent(facilityId)}/operating-hours/projection?local_date=${encodeURIComponent(localDate)}`,
  );
}

export function getOperatingHoursProjections(
  facilityId: string,
  startsOn: string,
  days: number,
): Promise<FacilityOperatingHoursProjectionPage> {
  return httpClient.get<FacilityOperatingHoursProjectionPage>(
    `/facilities/${encodeURIComponent(facilityId)}/operating-hours/projections?starts_on=${encodeURIComponent(startsOn)}&days=${encodeURIComponent(String(days))}`,
  );
}

// ---------------------------------------------------------------------------
// Cancellation policy — PUT /facilities/{id}/cancellation-policy
// fee_percentage is the cancellation fee (0 = full refund, 100 = no refund).
// ---------------------------------------------------------------------------

// Wire shape of one cancellation window. fee_percentage is a branded RatePercent
// (ADR-005) — the cancellation fee withheld (0 = full refund, 100 = no refund).
// This is API data; component edit state uses its own local form type.
export interface CancellationWindow {
  hours_before: number;
  fee_percentage: RatePercent;
}

export interface CancellationPolicyResponse {
  policy_id: string;
  facility_id: string;
  windows: CancellationWindow[];
  created_at: string;
}

export function getCancellationPolicy(
  facilityId: string,
): Promise<CancellationPolicyResponse> {
  return httpClient.get<CancellationPolicyResponse>(
    `/facilities/${facilityId}/cancellation-policy`,
  );
}

export function setCancellationPolicy(
  facilityId: string,
  windows: CancellationWindow[],
): Promise<CancellationPolicyResponse> {
  return httpClient.put<CancellationPolicyResponse>(
    `/facilities/${facilityId}/cancellation-policy`,
    { windows },
  );
}

// ---------------------------------------------------------------------------
// Liability waiver — POST /facilities/{id}/waiver (publish), GET returns current
// ---------------------------------------------------------------------------

export interface WaiverVersion {
  version_id: string;
  facility_id: string;
  version_number: number;
  content: string;
  content_hash: string;
  published_at: string;
  published_by: string;
}

export function getFacilityWaiver(
  facilityId: string,
): Promise<WaiverVersion | null> {
  return httpClient.get<WaiverVersion | null>(
    `/facilities/${facilityId}/waiver`,
  );
}

export function publishFacilityWaiver(
  facilityId: string,
  content: string,
): Promise<WaiverVersion> {
  return httpClient.post<WaiverVersion>(`/facilities/${facilityId}/waiver`, {
    content,
  });
}

// ---------------------------------------------------------------------------
// Membership plans — facility-scoped subscription / visit-pack offerings.
//   GET   /facilities/{facilityId}/membership-plans      (list)
//   POST  /facilities/{facilityId}/membership-plans      (create)
//   PATCH /facilities/membership-plans/{planId}          (update)
// Mirrors backend MembershipPlan{Response,Create,Update} in common/models.py.
// plan_type is the billing cadence: monthly | quarterly | annual | visits_card.
// status is active | inactive — plans are deactivated, never deleted (no DELETE).
// price is a Decimal: number | string at runtime (Pydantic serializes for precision).
// ---------------------------------------------------------------------------

export type MembershipPlanType =
  | "monthly"
  | "quarterly"
  | "annual"
  | "visits_card";

export type MembershipPlanStatus = "active" | "inactive";

// MembershipPlan / MembershipPlanCreate — aliased to the generated types
// (ADR-005) so `price` stays a branded Money (Decimal string), never a plain
// number/string. plan_type is the billing cadence; status is active | inactive
// (plans are deactivated, never deleted). The hand-written mirrors are retired.
export type MembershipPlan = Schema["MembershipPlanResponse"];

export type MembershipPlanCreate = Schema["MembershipPlanCreate"];

export type MembershipPlanUpdate = Schema["MembershipPlanUpdate"];

// Entitlements (Slice 6.2) — a plan's "N included sessions per period" (or
// free-entry / reduced-price) config. The plan editor UI (membership-plans.tsx)
// only exposes ONE case (service="classes", benefit_type="included_sessions") as
// a single numeric field; the other benefit_type/service combinations are real
// backend fields but not yet surfaced in a control (future extension, see the
// comment on IncludedClassesField below). Any entry a plan already carries that
// this form doesn't render is preserved verbatim on save, never dropped.
export type MembershipEntitlementCreate = Schema["MembershipEntitlementCreate"];

export type MembershipEntitlementResponse =
  Schema["MembershipEntitlementResponse"];

export function listMembershipPlans(
  facilityId: string,
): Promise<MembershipPlan[]> {
  return httpClient.get<MembershipPlan[]>(
    `/facilities/${facilityId}/membership-plans`,
  );
}

export function createMembershipPlan(
  facilityId: string,
  body: MembershipPlanCreate,
): Promise<MembershipPlan> {
  return httpClient.post<MembershipPlan>(
    `/facilities/${facilityId}/membership-plans`,
    body,
  );
}

export function updateMembershipPlan(
  planId: string,
  body: MembershipPlanUpdate,
): Promise<MembershipPlan> {
  return httpClient.patch<MembershipPlan>(
    `/facilities/membership-plans/${planId}`,
    body,
  );
}
